"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Camera = void 0;
const React = require("react");
const react_1 = require("react");
const cssComposer_1 = require("../../../../helpers/cssComposer");
const CameraFeedCustom_1 = require("./components/CameraFeedCustom");
const CameraFeedNative_1 = require("./components/CameraFeedNative");
const CameraOptions_1 = require("./components/CameraOptions");
const CameraPreview_1 = require("./components/CameraPreview");
const styles = require("./styles.scss");
const CameraFeedPlaceholder_1 = require("./components/CameraFeedPlaceholder");
const css = (0, cssComposer_1.default)(styles);
/**
 * This component can have side effects related to the browser that it is being used on, known issues that can arise:
 * - WebKit:
 *  - If you are using a library that overwrites events, like "react-fastclick" or "fastclick",
 * this will affect how much WebKit trusts that event, and WebKit will not open the file dialog/native camera.
 */
const Camera = ({ fastShoot, mode, returnType, videoEnvironment, videoWidth, onPhotoTaken, isRoundStyle = false, previewIcon = "photo-solid" }) => {
    const cameraFeedCustomRef = (0, react_1.createRef)();
    const cameraFeedNativeRef = (0, react_1.createRef)();
    const [facingMode, setFacingMode] = (0, react_1.useState)(videoEnvironment);
    const [errorRenderingCustomCamera, setErrorRenderingCustomCamera] = (0, react_1.useState)(false);
    const [image, setImage] = (0, react_1.useState)(null); // content captured by camera.
    const browserSupportExists = !!window.navigator.mediaDevices;
    (0, react_1.useEffect)(() => {
        setFacingMode(videoEnvironment);
    }, [videoEnvironment]);
    const containerStyle = {
        height: videoWidth,
        width: videoWidth
    };
    const getMode = () => {
        // if native, always return native.
        if (mode === "native") {
            return "native";
        }
        // if auto, and either no browser support or permission denied, return native.
        if (mode === "auto" &&
            (!browserSupportExists || errorRenderingCustomCamera)) {
            return "native";
        }
        // if not native or auto, return custom.
        if (mode === "custom") {
            return "custom";
        }
        // if auto, and both browser support and permissions expected, return custom.
        if (mode === "auto" &&
            !errorRenderingCustomCamera &&
            browserSupportExists) {
            return "custom";
        }
    };
    const getFeed = () => {
        // there is an image, we don't want to show the feed.
        if (image !== null) {
            return null;
        }
        const feedMode = getMode();
        if (feedMode === "native") {
            return (React.createElement(CameraFeedNative_1.CameraFeedNative, { videoEnvironment: videoEnvironment, ref: cameraFeedNativeRef }));
        }
        if (feedMode === "custom") {
            return (React.createElement("div", { style: containerStyle },
                React.createElement(CameraFeedCustom_1.CameraFeedCustom, { onErrorRenderingCamera: () => setErrorRenderingCustomCamera(true), facingMode: facingMode, ref: cameraFeedCustomRef, width: videoWidth, round: isRoundStyle })));
        }
    };
    const takePhotoClicked = async () => {
        let newImage;
        if (getMode() === "native") {
            newImage = await cameraFeedNativeRef.current.capturePhoto(returnType);
        }
        else {
            newImage = await cameraFeedCustomRef.current.capturePhoto(returnType);
        }
        /**
         * If fastshoot enabled, do not wait for user input, just call onChange method and reset state.
         * Otherwise ignore and let the user decide whether to accept or reject.
         *
         * If the camera is in native mode, they have already accepted the image using the native UI.
         */
        if (fastShoot || getMode() === "native") {
            if (newImage) {
                onPhotoTaken(newImage);
                setImage(null);
                // feedback to the click event target so that it can update its own state accordingly
                return newImage; // success
            }
            else {
                return null; // canncellation
            }
        }
        setImage(newImage);
        return newImage;
    };
    const cameraOptions = () => {
        const state = image ? "confirmPhoto" : "takePhoto";
        return (React.createElement(CameraOptions_1.CameraOptions, { onTakePhoto: takePhotoClicked, optionsState: state, onPhotoAccepted: () => {
                onPhotoTaken(image);
                setImage(null);
            }, onPhotoRejected: () => {
                setImage(null);
            }, showToggleCamera: getMode() === "custom", onCameraToggle: () => {
                setFacingMode(facingMode === "user" ? "environment" : "user");
            } }));
    };
    const cameraPreview = () => {
        if (getMode() === "native") {
            return (React.createElement("div", { style: containerStyle },
                React.createElement(CameraFeedPlaceholder_1.CameraFeedPlaceholder, { icon: previewIcon, onClick: takePhotoClicked, round: isRoundStyle })));
        }
        if (image === null) {
            return null;
        }
        return (React.createElement("div", { style: containerStyle },
            React.createElement(CameraPreview_1.CameraPreview, { image: image, round: isRoundStyle })));
    };
    return (React.createElement("div", { className: css("cameraContainer"), "data-test": "camera" },
        getFeed(),
        cameraPreview(),
        cameraOptions()));
};
exports.Camera = Camera;
//# sourceMappingURL=index.js.map